// Canvas 2D 棋盘+棋子渲染 (无外部素材)
// 参考文档: MDN Canvas API (fillRect/arc/fillText/beginPath/moveTo/lineTo/stroke)

import {
  GameState, Piece, Square,
  fileOf, rankOf, makeSquare,
  CHAR_RED, CHAR_BLACK,
} from '../game/types';
import { dragDepthScale, type PieceMotionSnapshot } from './pieceMotion';

export interface BoardLayout {
  cellSize: number;   // 一格像素
  margin: number;     // 外边距
  width: number;
  height: number;
}

export const DEFAULT_LAYOUT: BoardLayout = {
  cellSize: 60,
  margin: 30,
  width: 30 * 2 + 8 * 60, // 540
  height: 30 * 2 + 9 * 60, // 600
};

export type PieceVisualPhase = 'idle' | 'selected' | 'dragging';

export interface PieceVisualMetrics {
  thickness: number;
  scale: number;
  lift: number;
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

export function pieceVisualMetrics(
  phase: PieceVisualPhase,
  depthScale = 1,
  liftProgress = 1,
): PieceVisualMetrics {
  if (phase === 'dragging') {
    return { thickness: 9, scale: 1.06 * depthScale, lift: 7 };
  }
  if (phase === 'selected') {
    const progress = clamp01(liftProgress);
    return {
      thickness: 4 + 3 * progress,
      scale: 1 + 0.06 * progress,
      lift: 5 * progress,
    };
  }
  return { thickness: 4, scale: 1, lift: 0 };
}

export function landingScale(progress: number): { x: number; y: number } {
  const wave = Math.sin(clamp01(progress) * Math.PI * 2);
  return { x: 1 + wave * 0.08, y: 1 - wave * 0.08 };
}

export function tacticalRippleOpacity(ripple: number): number {
  return clamp01(ripple) * 0.55;
}

// 像素 → 方格. flip=true 时棋盘 180° 旋转 (玩家执黑时使用)
export function pixelToSquare(layout: BoardLayout, x: number, y: number, flip = false): Square | null {
  let f: number, r: number;
  if (flip) {
    f = 8 - Math.round((x - layout.margin) / layout.cellSize);
    r = Math.round((y - layout.margin) / layout.cellSize);
  } else {
    f = Math.round((x - layout.margin) / layout.cellSize);
    r = 9 - Math.round((y - layout.margin) / layout.cellSize);
  }
  if (f < 0 || f > 8 || r < 0 || r > 9) return null;
  return makeSquare(f, r);
}

// 方格 → 像素中心. flip=true 时棋盘 180° 旋转
export function squareToPixel(layout: BoardLayout, sq: Square, flip = false): { x: number; y: number } {
  const f = fileOf(sq), r = rankOf(sq);
  if (flip) {
    return {
      x: layout.margin + (8 - f) * layout.cellSize,
      y: layout.margin + r * layout.cellSize,
    };
  }
  return {
    x: layout.margin + f * layout.cellSize,
    y: layout.margin + (9 - r) * layout.cellSize,
  };
}

// 绘制空棋盘 (一次性, 可缓存到 offscreen)
export function drawBoardBg(ctx: CanvasRenderingContext2D, layout: BoardLayout): void {
  const { cellSize: c, margin: m, width, height } = layout;
  // 底
  ctx.fillStyle = '#f1d9a0';
  ctx.fillRect(0, 0, width, height);
  // 木纹效果 (随机纹路可省略)
  ctx.strokeStyle = '#6b4423';
  ctx.lineWidth = 1.5;
  // 9 纵线 (rank 0..9). 注意: 楚河汉界处, 第 0/8 纵线纵贯全盘; 中间 1-7 纵线在河处断开.
  for (let f = 0; f < 9; f++) {
    const x = m + f * c;
    ctx.beginPath();
    if (f === 0 || f === 8) {
      ctx.moveTo(x, m);
      ctx.lineTo(x, m + 9 * c);
    } else {
      // 上半 (rank 9..5)
      ctx.moveTo(x, m);
      ctx.lineTo(x, m + 4 * c);
      // 下半 (rank 4..0)
      ctx.moveTo(x, m + 5 * c);
      ctx.lineTo(x, m + 9 * c);
    }
    ctx.stroke();
  }
  // 10 横线
  for (let r = 0; r < 10; r++) {
    const y = m + r * c;
    ctx.beginPath();
    ctx.moveTo(m, y);
    ctx.lineTo(m + 8 * c, y);
    ctx.stroke();
  }
  // 九宫对角线 (红: rank 0-2, 黑: rank 7-9)
  ctx.beginPath();
  // 红九宫
  ctx.moveTo(m + 3 * c, m + 7 * c); ctx.lineTo(m + 5 * c, m + 9 * c);
  ctx.moveTo(m + 5 * c, m + 7 * c); ctx.lineTo(m + 3 * c, m + 9 * c);
  // 黑九宫
  ctx.moveTo(m + 3 * c, m + 0 * c); ctx.lineTo(m + 5 * c, m + 2 * c);
  ctx.moveTo(m + 5 * c, m + 0 * c); ctx.lineTo(m + 3 * c, m + 2 * c);
  ctx.stroke();
  // 楚河汉界
  ctx.fillStyle = '#6b4423';
  ctx.font = 'bold 24px "STKaiti","KaiTi","serif"';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const riverY = m + 4.5 * c;
  ctx.fillText('楚  河', m + 2 * c, riverY);
  ctx.fillText('漢  界', m + 6 * c, riverY);
  // 兵/炮位标记 (小十字). 位置: 红方 a3,c3,e3,g3,i3,b2,h2; 黑方对应 rank 6/7
  const marks: Array<[number, number]> = [
    [0, 3], [2, 3], [4, 3], [6, 3], [8, 3],  // 红兵
    [1, 2], [7, 2],                            // 红炮
    [0, 6], [2, 6], [4, 6], [6, 6], [8, 6],  // 黑卒
    [1, 7], [7, 7],                            // 黑炮
  ];
  ctx.strokeStyle = '#6b4423';
  ctx.lineWidth = 1;
  for (const [f, r] of marks) {
    const { x, y } = squareToPixel(layout, makeSquare(f, r));
    drawCross(ctx, x, y, 5);
  }
}

function drawCross(ctx: CanvasRenderingContext2D, x: number, y: number, _size: number): void {
  // 在交叉点四角画小 L 形 (兵/炮位标记)
  const gap = 8; // 距交叉点像素
  const len = 5;  // L 边长
  ctx.beginPath();
  // 左上 L (除非在最左)
  // 这里简化: 都画 4 个 L (边界兵位退化为 2 个)
  const drawL = (dx: number, dy: number) => {
    ctx.moveTo(x + dx * gap, y + dy * (gap + len));
    ctx.lineTo(x + dx * gap, y + dy * gap);
    ctx.lineTo(x + dx * (gap + len), y + dy * gap);
  };
  drawL(1, 1); drawL(-1, 1); drawL(1, -1); drawL(-1, -1);
  ctx.stroke();
}

const boardBackgroundCache = new Map<string, HTMLCanvasElement>();

function drawCachedBoardBg(ctx: CanvasRenderingContext2D, layout: BoardLayout): void {
  if (typeof document === 'undefined') {
    drawBoardBg(ctx, layout);
    return;
  }
  const key = `${layout.width}:${layout.height}:${layout.cellSize}:${layout.margin}`;
  let cached = boardBackgroundCache.get(key);
  if (!cached) {
    cached = document.createElement('canvas');
    cached.width = layout.width;
    cached.height = layout.height;
    const cachedCtx = cached.getContext('2d');
    if (!cachedCtx) {
      drawBoardBg(ctx, layout);
      return;
    }
    drawBoardBg(cachedCtx, layout);
    boardBackgroundCache.set(key, cached);
  }
  ctx.drawImage(cached, 0, 0);
}

interface DrawPieceOptions {
  highlight?: boolean;
  ghost?: boolean;
  userColor?: 'r' | 'b';
  phase?: PieceVisualPhase;
  liftProgress?: number;
  depthScale?: number;
  landingProgress?: number;
}

function drawPieceShadow(
  ctx: CanvasRenderingContext2D,
  radius: number,
  visual: PieceVisualMetrics,
): void {
  const raised = visual.lift > 0;
  ctx.save();
  ctx.translate(-visual.lift * 0.32, visual.thickness * 0.72 + visual.lift * 0.38);
  ctx.scale(1, raised ? 0.56 : 0.48);
  ctx.beginPath();
  ctx.arc(0, 0, radius * (raised ? 0.9 : 0.84), 0, Math.PI * 2);
  ctx.fillStyle = raised ? 'rgba(73, 39, 17, 0.22)' : 'rgba(73, 39, 17, 0.28)';
  ctx.shadowColor = raised ? 'rgba(60, 31, 14, 0.28)' : 'rgba(60, 31, 14, 0.24)';
  ctx.shadowBlur = raised ? 9 : 4;
  ctx.fill();
  ctx.restore();
}

function drawPieceSide(
  ctx: CanvasRenderingContext2D,
  radius: number,
  topY: number,
  thickness: number,
): void {
  const sideGradient = ctx.createLinearGradient(-radius, topY, radius, topY);
  sideGradient.addColorStop(0, '#75431f');
  sideGradient.addColorStop(0.42, '#aa7139');
  sideGradient.addColorStop(0.72, '#8d572b');
  sideGradient.addColorStop(1, '#643817');
  for (let offset = Math.ceil(thickness); offset >= 1; offset--) {
    ctx.beginPath();
    ctx.arc(0, topY + offset, radius, 0, Math.PI * 2);
    ctx.fillStyle = sideGradient;
    ctx.fill();
  }
  ctx.beginPath();
  ctx.arc(0, topY + thickness, radius, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(76, 39, 15, 0.72)';
  ctx.lineWidth = 1.5;
  ctx.stroke();
}

function drawPieceFace(
  ctx: CanvasRenderingContext2D,
  radius: number,
  topY: number,
  piece: Piece,
): void {
  const ink = piece.color === 'r' ? '#a51f18' : '#202927';
  const faceGradient = ctx.createRadialGradient(
    -radius * 0.34,
    topY - radius * 0.38,
    radius * 0.08,
    0,
    topY,
    radius * 1.08,
  );
  faceGradient.addColorStop(0, '#fff2ca');
  faceGradient.addColorStop(0.5, '#efd49b');
  faceGradient.addColorStop(0.82, '#ddb875');
  faceGradient.addColorStop(1, '#bd8748');

  ctx.beginPath();
  ctx.arc(0, topY, radius, 0, Math.PI * 2);
  ctx.fillStyle = faceGradient;
  ctx.fill();
  ctx.strokeStyle = ink;
  ctx.lineWidth = 2.4;
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(0, topY + 0.8, radius - 5, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(103, 56, 24, 0.42)';
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(0, topY - 0.5, radius - 5, 0, Math.PI * 2);
  ctx.strokeStyle = ink;
  ctx.lineWidth = 1.05;
  ctx.stroke();

  ctx.save();
  ctx.translate(-radius * 0.28, topY - radius * 0.36);
  ctx.rotate(-0.28);
  ctx.scale(1, 0.42);
  ctx.beginPath();
  ctx.arc(0, 0, radius * 0.25, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.34)';
  ctx.fill();
  ctx.restore();
}

function drawPieceInscription(
  ctx: CanvasRenderingContext2D,
  layout: BoardLayout,
  topY: number,
  piece: Piece,
  userColor?: 'r' | 'b',
): void {
  const char = piece.color === 'r' ? CHAR_RED[piece.type] : CHAR_BLACK[piece.type];
  ctx.fillStyle = piece.color === 'r' ? '#a51f18' : '#202927';
  ctx.font = `bold ${Math.floor(layout.cellSize * 0.55)}px "STKaiti","KaiTi","serif"`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.save();
  if (userColor !== undefined && piece.color !== userColor) {
    ctx.translate(0, topY);
    ctx.rotate(Math.PI);
    ctx.fillText(char, 0, 0);
  } else {
    ctx.fillText(char, 0, topY);
  }
  ctx.restore();
}

function drawPieceAt(
  ctx: CanvasRenderingContext2D,
  layout: BoardLayout,
  x: number,
  y: number,
  piece: Piece,
  options: DrawPieceOptions = {},
): void {
  const visual = pieceVisualMetrics(
    options.phase ?? 'idle',
    options.depthScale ?? 1,
    options.liftProgress ?? 1,
  );
  const landing = options.landingProgress === undefined
    ? { x: 1, y: 1 }
    : landingScale(options.landingProgress);
  const radius = layout.cellSize * 0.42;
  const topY = -visual.lift - visual.thickness * 0.5;

  ctx.save();
  ctx.translate(x, y);
  ctx.scale(visual.scale * landing.x, visual.scale * landing.y);
  if (options.ghost) ctx.globalAlpha = 0.5;
  if (options.highlight) {
    ctx.beginPath();
    ctx.arc(0, 1, radius + 7, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(208, 158, 58, 0.16)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(151, 100, 26, 0.48)';
    ctx.lineWidth = 2;
    ctx.stroke();
  }
  drawPieceShadow(ctx, radius, visual);
  drawPieceSide(ctx, radius, topY, visual.thickness);
  drawPieceFace(ctx, radius, topY, piece);
  drawPieceInscription(ctx, layout, topY, piece, options.userColor);
  ctx.restore();
}

// 绘制单棋子
// userColor: 用户执方 — 棋盘 180° 翻转 + 对方棋子文字旋转 180°
export function drawPiece(
  ctx: CanvasRenderingContext2D,
  layout: BoardLayout,
  sq: Square,
  piece: Piece,
  options: { highlight?: boolean; ghost?: boolean; userColor?: 'r' | 'b' } = {},
): void {
  const flip = options.userColor === 'b';
  const { x, y } = squareToPixel(layout, sq, flip);
  drawPieceAt(ctx, layout, x, y, piece, options);
}

// 全量重绘 (棋盘 + 全部棋子 + 高亮)
export function redraw(
  ctx: CanvasRenderingContext2D,
  layout: BoardLayout,
  state: GameState,
  options: {
    selected?: Square | null;
    legalTargets?: Square[];
    lastMove?: { from: Square; to: Square } | null;
    drag?: { square: Square; piece: Piece; x: number; y: number } | null;
    userColor?: 'r' | 'b';
    motion?: PieceMotionSnapshot;
  } = {},
): void {
  const userColor = options.userColor;
  const flip = userColor === 'b';
  drawCachedBoardBg(ctx, layout);
  // 上次走子起讫点: 浅红圆盘 (棋子之下, 半透明)
  if (options.lastMove) {
    const haloR = layout.cellSize * 0.46;
    for (const sq of [options.lastMove.from, options.lastMove.to]) {
      const { x, y } = squareToPixel(layout, sq, flip);
      ctx.beginPath();
      ctx.arc(x, y, haloR, 0, 2 * Math.PI);
      ctx.fillStyle = 'rgba(210, 90, 90, 0.28)';
      ctx.fill();
    }
  }
  // 合法目标点
  if (options.legalTargets && options.legalTargets.length > 0) {
    for (const t of options.legalTargets) {
      const { x, y } = squareToPixel(layout, t, flip);
      ctx.beginPath();
      ctx.arc(x, y, 8, 0, 2 * Math.PI);
      ctx.fillStyle = '#3c3';
      ctx.fill();
    }
  }
  if (options.motion?.landing && options.motion.landing.ripple > 0) {
    const { x, y } = squareToPixel(layout, options.motion.landing.square, flip);
    const expansion = 0.45 + options.motion.landing.progress * 0.72;
    ctx.save();
    ctx.translate(x, y + layout.cellSize * 0.08);
    ctx.scale(1, 0.46);
    ctx.beginPath();
    ctx.arc(0, 0, layout.cellSize * expansion, 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(145, 67, 43, ${tacticalRippleOpacity(options.motion.landing.ripple)})`;
    ctx.lineWidth = 2.5;
    ctx.stroke();
    ctx.restore();
  }
  // 所有棋子 (跳过拖拽中棋子)
  for (let sq = 0; sq < 90; sq++) {
    const p = state.board[sq];
    if (!p) continue;
    if (options.drag && options.drag.square === sq) continue;
    const selected = options.motion?.selectedSquare === sq || options.selected === sq;
    const landing = options.motion?.landing?.square === sq ? options.motion.landing : null;
    const { x, y } = squareToPixel(layout, sq, flip);
    drawPieceAt(ctx, layout, x, y, p, {
      highlight: selected,
      userColor,
      phase: selected ? 'selected' : 'idle',
      liftProgress: selected ? options.motion?.selectedLift ?? 1 : 0,
      landingProgress: landing?.progress,
    });
  }
  // 上次走子落点: 紫色环包裹棋子 (绘制于棋子之上, 视觉上"裹住"新位置的棋子)
  if (options.lastMove) {
    const { x, y } = squareToPixel(layout, options.lastMove.to, flip);
    ctx.beginPath();
    ctx.arc(x, y, layout.cellSize * 0.42 + 4, 0, 2 * Math.PI);
    ctx.strokeStyle = '#8d3f32';
    ctx.lineWidth = 2.5;
    ctx.stroke();
  }
  // 拖拽棋子 — 保留抓取偏移，并根据显示空间的远近轻微缩放.
  if (options.drag) {
    const { piece, x, y } = options.drag;
    drawPieceAt(ctx, layout, x, y, piece, {
      userColor,
      phase: 'dragging',
      depthScale: dragDepthScale(y, layout.height),
    });
  }
}
